/**
 * Display names of the people in the approval rules (masterplan credit-limits-v3, B5). `GET /credit-limits/config`
 * answers `names` (lower-case address -> «First Last», from `etl.EmployeeMaster`); the API service hands every
 * answer to `setDisplayNames`, so the request dialogs, the grid and the chain preview show real names wherever the
 * page has loaded the config once (the page always does, for the roles). `nameFromEmail` in
 * `requests/chainPreview.ts` asks here first and derives a label from the address only for a person not listed.
 *
 * A module-level store, because the requests code reads names in plain functions (columns, status text) that have no
 * place to receive them; `useDisplayNamesVersion` lets a component re-render when new names arrive.
 */
import { useSyncExternalStore } from 'react';

let names: Record<string, string> = {};
let version = 0;
const listeners = new Set<() => void>();

const keyOf = (email: string | null | undefined) => (email ?? '').trim().toLowerCase();

/** Adds the names of an answer (never drops one); notifies only when something changed. */
export function setDisplayNames(next: Record<string, string> | null | undefined): void {
    if (!next) return;
    let changed = false;
    for (const [email, name] of Object.entries(next)) {
        const key = keyOf(email);
        const value = typeof name === 'string' ? name.trim() : '';
        if (key === '' || value === '' || names[key] === value) continue;
        names = { ...names, [key]: value };
        changed = true;
    }
    if (changed) {
        version++;
        listeners.forEach(l => l());
    }
}

/** The name of an address, or null when the config did not carry it. */
export function displayNameOf(email: string | null | undefined): string | null {
    return names[keyOf(email)] ?? null;
}

/** Test hook. */
export function resetDisplayNames(): void {
    names = {};
    version++;
    listeners.forEach(l => l());
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
const getVersion = () => version;

/** Changes whenever names were added; put it in a `useMemo` dependency list to rebuild name-bearing columns. */
export function useDisplayNamesVersion(): number {
    return useSyncExternalStore(subscribe, getVersion, getVersion);
}
